/**
 * RunState — mutated in place, struct-of-arrays, zero allocation after
 * initRun.
 *
 * Purity of the SYSTEM is guaranteed by determinism plus an explicit
 * hash/serializer, not by allocating a new state 60 times a second.
 * Iron Rule 1 says same inputs -> same outputs; it does not say immutable,
 * and at ~2500 moving entities immutability is a GC-stall machine.
 */
import { Phase, Outcome, STREAM_COUNT, Stream, TICK_RATE, type PhaseValue, type OutcomeValue } from './constants.js';
import { WORDS_PER_STREAM, seedStreams, nextRange } from './rng.js';
import type { GameConfig } from './config.js';

export interface PlayerMeta {
  readonly permanentUpgrades: Readonly<Record<string, number>>;
  readonly gear: Readonly<Record<string, string>>;
  readonly chapterUnlocked: number;
}

export const EMPTY_META: PlayerMeta = { permanentUpgrades: {}, gear: {}, chapterUnlocked: 1 };

export interface RunInit {
  readonly config: GameConfig;
  readonly meta: PlayerMeta;
  readonly seed: number;
  readonly chapter: number;
  /** Which roster character leads this run. Part of init: deterministic. */
  readonly characterIdx?: number;
  /** 0 (default) = MEANINGFUL FAILURE, 1 = THRESHOLD AT THE BOSS. See RunState.variant. */
  readonly variant?: number;
}

/** Fixed-capacity pool with a free list. Never grows during a run. */
export interface Pool {
  readonly cap: number;
  count: number;
  /** free[0..freeCount) holds available slots. */
  readonly free: Int32Array;
  freeCount: number;
  readonly alive: Uint8Array;
}

function makePool(cap: number): Pool {
  const free = new Int32Array(cap);
  for (let i = 0; i < cap; i++) free[i] = cap - 1 - i;
  return { cap, count: 0, free, freeCount: cap, alive: new Uint8Array(cap) };
}

export function poolAlloc(p: Pool): number {
  if (p.freeCount === 0) return -1; // Full: drop the spawn. Never grow.
  const idx = p.free[--p.freeCount];
  p.alive[idx] = 1;
  p.count++;
  return idx;
}

export function poolFree(p: Pool, idx: number): void {
  if (p.alive[idx] === 0) return;
  p.alive[idx] = 0;
  p.free[p.freeCount++] = idx;
  p.count--;
}

export interface RunState {
  readonly config: GameConfig;
  readonly seed: number;
  readonly chapter: number;

  tick: number;
  phase: PhaseValue;
  outcome: OutcomeValue;
  worldY: number;

  squadSize: number;
  squadHpPool: number;
  /** High-water mark. The reference a "how close to death am I" reading
   *  needs — without it the renderer has to invent a denominator. */
  squadPeak: number;
  playerX: number;
  playerY: number;

  level: number;
  xp: number;
  xpToNext: number;
  killCount: number;
  damageDealt: number;

  bossIdx: number;
  bossPhase: number;
  bossSpawned: boolean;

  waveCursor: number;
  /** Per-wave spawn progress, so a wave can stagger over its duration
   *  without the tick loop needing to re-derive history. */
  readonly waveSpawned: Int32Array;
  /** Fractional bodies owed by the continuous flow spawner. */
  flowAcc: number;
  /** Monotonic conveyor counter — drives rank/file packing, not RNG. */
  flowSeq: number;
  /** Roster index chosen at init; stats and team colour derive from it. */
  readonly characterIdx: number;
  /** True when the run ended because the chapter boss reached the line. */
  lostToBoss: boolean;
  /**
   * 0 = normal (MEANINGFUL FAILURE): squad can wipe to 0 mid-run, the run
   * ends there. 1 = THRESHOLD AT THE BOSS: damageSquad floors squadSize at
   * 1 (never a mid-run wipe) and the run resolves the instant the boss
   * spawns, comparing squadSize against `clashThreshold` rather than
   * fighting the boss's hp. Founder spec, 2026-08-21 (`?variant=A`/`B`).
   */
  variant: number;
  /** T. Fixed per rules.csv `clash_threshold_t`; only load-bearing when variant===1. */
  clashThreshold: number;
  /** squadSize at the moment variant 1 resolved, -1 until then. For the
   *  end-of-run "crowd vs threshold" readout. */
  clashCrowd: number;
  /** 0-3, computed the same tick clashCrowd is set (variant 1 only): 1 at
   *  clashThreshold, 2 at 1.15x, 3 at 1.3x. 0 until resolved or if lost. */
  stars: number;
  /**
   * Ticks left on a deployed vehicle. A vehicle is a TIMED loadout, not a
   * body: it multiplies damage and adds its own guns while it lasts, so
   * claiming one is a burst of power you have to spend rather than a
   * permanent step up the ladder.
   */
  vehicleTicks: number;
  /**
   * A-43. Ticks left of full invulnerability (0 = not dodging) and ticks
   * left before another dodge command is accepted (0 = ready). Both count
   * down every tick regardless of phase transitions elsewhere, same as
   * vehicleTicks above.
   */
  dodgeActiveTicks: number;
  dodgeCooldownTicks: number;
  gateCursor: number;
  pendingLevelUps: number;
  /**
   * A-45. Ticks left in the current level-up's non-blocking choice window
   * (0 = no active window). While active, `offerOptions[0..offerCount)`
   * holds the offered upgrade indices for the renderer to display.
   */
  boonWindowTicks: number;
  rerollsLeft: number;
  revivesLeft: number;
  elapsedFrozenTicks: number;

  weaponIdx: number;
  fireCooldown: number;

  readonly rng: Uint32Array;

  offerKind: number;
  readonly offerOptions: Int32Array;
  offerCount: number;
  readonly upgradeStacks: Int32Array;

  // Derived-stat cache — recomputed on every upgrade/gate application.
  statMoveSpeed: number;
  statDamage: number;
  statFireRate: number;
  statProjectiles: number;
  statPierce: number;
  statMaxHp: number;
  statPickupRadius: number;

  // Enemies
  readonly ep: Pool;
  readonly eX: Float64Array; readonly eY: Float64Array;
  readonly eHp: Float64Array; readonly eCooldown: Float64Array;
  readonly eDefIdx: Int32Array;
  /**
   * Boss-ness is a property of the SPAWN, not of the enemy type. A Rift
   * Brute that arrives in the horde is a brute; the same def spawned as a
   * chapter boss is a boss -- it holds the line instead of breaching, is
   * authored at full strength instead of tier-scaled, and shrugs off
   * knockback. Before this, `is_boss` was 0 for every chapter boss except
   * the last, so chapter bosses were quietly treated as ordinary horde.
   */
  readonly eIsBoss: Uint8Array;
  /**
   * The lane this body was AUTHORED into at spawn (waves.csv `lane`, or
   * the pattern's own draw when authored -1) -- never updated after spawn,
   * even once D13 crossover or the separation solver moves the body's
   * actual `eX` around. Lane-gates convergence: a body may only be pulled
   * onto the player's exact position (step.ts, "D13 CROSSOVER") when the
   * player currently occupies THIS lane, not whichever lane `eX` happens
   * to sit in at the moment of the check.
   */
  readonly eLane: Int32Array;

  // Player projectiles
  readonly pp: Pool;
  readonly pX: Float64Array; readonly pY: Float64Array;
  readonly pVx: Float64Array; readonly pVy: Float64Array;
  readonly pLife: Float64Array; readonly pDamage: Float64Array;
  readonly pPierce: Int32Array;

  // Enemy projectiles
  readonly qp: Pool;
  readonly qX: Float64Array; readonly qY: Float64Array;
  readonly qVx: Float64Array; readonly qVy: Float64Array;
  readonly qLife: Float64Array; readonly qDamage: Float64Array;

  // Pickups / XP orbs
  readonly kp: Pool;
  readonly kX: Float64Array; readonly kY: Float64Array;
  readonly kKind: Int32Array; readonly kValue: Int32Array;

  /**
   * MONOLITHS: the parallel objective.
   *
   * Not a gate. A gate arrives, is steered into, and resolves in seconds;
   * a monolith stands at the edge of the corridor for most of a minute
   * with a five-figure counter on it, and the only way to chip it is to
   * spend fire on it that is therefore NOT killing the horde. Ignoring one
   * costs nothing but the prize. That is the dilemma in its true form --
   * a continuous allocation of firepower, decided second by second,
   * instead of a lane you either enter or do not.
   *
   * Inside each is a TITAN, previewed as a hologram that materialises as
   * the rock cracks. Break it and the titan fights for the rest of the run.
   */
  readonly mp: Pool;
  readonly mX: Float64Array; readonly mY: Float64Array;
  /** Block payload op (BLOCK_* in step.ts) and its value. */
  readonly mOp: Int32Array; readonly mValue: Float64Array;
  readonly mHp: Float64Array; readonly mHpMax: Float64Array;
  readonly mLane: Int32Array;
  /** Which titan is entombed (index into the titan roster). */
  readonly mTitan: Int32Array;
  /** Ticks this monolith stays on the field before it is left behind. */
  readonly mLife: Float64Array;
  /**
   * The ACTUAL tick each scheduled monolith (`config.monoliths`, indexed
   * the same way) is raised at this run — `config.monoliths[i].atTick`
   * jittered by a seeded +/-0.35s draw, so the schedule isn't the same
   * metronome every run (docs/DECISIONS.md, "Monolith dilemma" pacing
   * finding) while staying fully deterministic per seed. Drawn once, in
   * schedule order, at init — never re-drawn.
   */
  readonly monolithRaiseTick: Int32Array;

  /**
   * ALLIES: monsters won from a charge station that FIGHT FOR YOU.
   *
   * The station's whole promise is that what you charge becomes real. A
   * multiplier or a stat bump cannot deliver that -- the player has to see
   * the thing they paid for walk into the horde and start killing. So an
   * ally is a first-class body in the sim: it advances up the corridor,
   * damages what it touches, takes damage back, and dies.
   */
  readonly lp: Pool;
  readonly lX: Float64Array; readonly lY: Float64Array;
  readonly lHp: Float64Array; readonly lHpMax: Float64Array;
  readonly lCooldown: Float64Array;
  /** Damage per swing and the radius it sweeps. */
  readonly lPower: Float64Array; readonly lRadius: Float64Array;
  /** Which titan this ally IS, so the renderer draws the promised body. */
  readonly lKind: Int32Array;

  /**
   * ARMOUR won from a charge station: a pool that soaks damage before the
   * squad does. Bodies are the score, so spending armour instead of bodies
   * is the only way to take a hit for free.
   */
  shieldHp: number;
  shieldMax: number;

  /**
   * Soldier formation positions (Contact and Impact, "squad bodies" part
   * 1). Recomputed fresh every tick from squadSize/playerX/playerY --
   * there is no independent per-soldier spawn/death lifecycle the way
   * enemies have one, so `alive`/`count` are derived directly each tick
   * in step.ts rather than through poolAlloc/poolFree.
   *
   * WORLD-SPACE (render) coordinates, NOT corridor/sim-space like eX/eY.
   * Ported verbatim from scene3d.ts's per-soldier position.set() so the
   * on-screen formation is pixel-identical to before the port -- see the
   * port site in step.ts for the mirrored constants and the exact
   * renderer lines each one cites. A future pass that wants these in
   * sim-space for collision math is free to add that; this port's only
   * job was moving the existing formula, unchanged.
   */
  readonly sp: Pool;
  readonly sX: Float64Array; readonly sY: Float64Array;
  /**
   * Scratch buffers for step.ts's per-tick formation `shape()` closure
   * (AUDIT A-13, hot-loop allocation). Pre-allocated once here instead of
   * `shape()` building an array of `{x,y}` objects every tick -- at
   * squadSize 16-20 that was 1000+ small object allocations/sec for the
   * whole run. Two independent pairs because a tick that loses bodies
   * needs BOTH the current formation (for the live squad) and the
   * pre-damage one (for "death in place") live at once.
   */
  readonly shapeScratchX: Float64Array; readonly shapeScratchY: Float64Array;
  readonly shapeScratchOldX: Float64Array; readonly shapeScratchOldY: Float64Array;
  /** Nearest-slot selection scratch for the death-in-place branch below. */
  readonly shapeScratchUsed: Uint8Array;

  // Gates in flight
  readonly gp: Pool;
  readonly gY: Float64Array; readonly gValue: Float64Array; readonly gHp: Float64Array;
  readonly gGroup: Int32Array; readonly gOp: Int32Array; readonly gLane: Int32Array;
  /** THE DIAL (founder, 2026-08-22): per-lane authored increment a
   * projectile hit adds to `gValue` for add/subtract/multiply lanes
   * (gates.csv `pump_per_hit`). 0 for every chapter but 12 -- inert,
   * current behaviour exactly. */
  readonly gPump: Float64Array;

  // Spatial hash over enemies
  readonly cellHead: Int32Array;
  readonly cellNext: Int32Array;
  readonly gridCols: number;
  readonly gridRows: number;
  readonly cellSize: number;

  /** Per-tick event buffer for the renderer. Sim never reads it back. */
  readonly events: number[];
}

export const CELL_SIZE = 2;

/**
 * MIRROR -- source of truth is step.ts's DECK_INVISIBLE_EDGE_UNITS (240,
 * itself mirroring scene3d.ts's deck far-edge-fade zone). Duplicated here
 * rather than imported because step.ts imports RunState/poolAlloc/
 * poolFree FROM this module -- importing back would be circular.
 *
 * The enemy spatial-hash grid below must span every depth an enemy can
 * actually occupy. It used to be sized to `arenaHeight + spawnAhead`
 * because every spawn origin (waves, conveyor) was within spawnAhead of
 * the player. The conveyor and corridor_mass waves now originate at
 * DECK_INVISIBLE_EDGE_UNITS_MIRROR instead (founder finding, 2026-09-06,
 * "stream in from the invisible edge") -- leaving the old sizing would
 * make rebuildGrid's own out-of-range clamp (step.ts, `cy >= gridRows`)
 * silently fold every enemy beyond the old, narrower depth into ONE grid
 * row regardless of how much farther out it actually is, turning
 * enemy-vs-enemy separation (also step.ts) into an O(n^2) walk over
 * that single overflowing bucket instead of a real spatial-hash lookup.
 */
const DECK_INVISIBLE_EDGE_UNITS_MIRROR = 240;
/**
 * Margin for how far a single wave's ranks can spread past that origin
 * (corridor_mass: `topY + rank * waveRankGap`, step.ts). The largest
 * authored wave today is 1069 bodies (config/balance/waves.csv) which,
 * at wave_rank_gap_units=1.3 and a typical 17-wide lane, spans ~82 ranks
 * (~107 units) -- 150 stays comfortably ahead of that without this file
 * hand-tracking every chapter's own wave counts.
 */
const WAVE_RANK_SPREAD_MARGIN_UNITS = 150;

export function initRun(init: RunInit): RunState {
  const { config, seed } = init;
  const r = config.rules;

  const ep = makePool(r.enemyPoolCap);
  const pp = makePool(r.projectilePoolCap);
  const qp = makePool(r.enemyProjectilePoolCap);
  const kp = makePool(r.pickupPoolCap);
  const gp = makePool(r.gatePoolCap);
  // Allies are few and large. A fixed small cap keeps them a set-piece --
  // an army of them would replace the player's own guns as the answer.
  const lp = makePool(8);
  // Two monoliths can overlap at an isle's midpoint; more would turn the
  // corridor into a shooting gallery of side-objectives.
  const mp = makePool(4);
  const sp = makePool(r.squadMax);

  const gridCols = Math.ceil(r.arenaWidth / CELL_SIZE) + 2;
  const maxEnemyDepthAhead = Math.max(
    r.spawnAhead,
    DECK_INVISIBLE_EDGE_UNITS_MIRROR + WAVE_RANK_SPREAD_MARGIN_UNITS,
  );
  const gridRows = Math.ceil((r.arenaHeight + maxEnemyDepthAhead) / CELL_SIZE) + 2;

  const rng = new Uint32Array(STREAM_COUNT * WORDS_PER_STREAM);
  seedStreams(rng, seed);

  // +/-0.35s seeded jitter per scheduled monolith, drawn once here in
  // schedule order (never re-drawn -- step.ts compares s.tick against
  // this precomputed array, not against config.monoliths[i].atTick
  // directly). +/-1.5s was requested first but measured to drop the
  // gap-to-next-wave floor to 0.90s (docs/DECISIONS.md, "monolith #2 ...
  // wave-gap outlier" — the fix that shipped assumed a static schedule);
  // +/-0.35s is the widest window that keeps every monolith's gap at or
  // above 2.0s at both jitter extremes.
  //
  // Stream.Combat, not a new Stream entry: unused by sim code today (no
  // other `Stream.Combat` read exists in step.ts), so drawing from it
  // cannot shift any other system's sequence -- Waves/Drops/UpgradeOffers
  // are all consumed repeatedly through a run and already tuned against
  // their current draw order. Stream.Cosmetic was ruled out: its own doc
  // comment (constants.ts) states it is "never read by sim code" and
  // this jitter is sim-behavioral (it changes WHEN a monolith raises),
  // not cosmetic.
  const JITTER_SEC = 0.35;
  const monolithRaiseTick = new Int32Array(config.monoliths.length);
  for (let i = 0; i < config.monoliths.length; i++) {
    const jitterSec = nextRange(rng, Stream.Combat, -JITTER_SEC, JITTER_SEC);
    monolithRaiseTick[i] = Math.max(1, config.monoliths[i]!.atTick + Math.round(jitterSec * TICK_RATE));
  }

  const s: RunState = {
    config, seed, chapter: init.chapter,
    tick: 0, phase: Phase.Running, outcome: Outcome.None, worldY: 0,
    squadSize: r.squadStart,
    squadHpPool: r.squadStart * r.squadUnitHp,
    squadPeak: r.squadStart,
    // Start ON a lane centre, not mid-corridor: the squad must never be
    // between lanes, including on tick zero.
    playerX: ((Math.floor(Math.max(1, r.laneCount) / 2) + 0.5) / Math.max(1, r.laneCount)) * r.arenaWidth,
    playerY: r.arenaHeight * 0.2,
    level: 1, xp: 0, xpToNext: config.xpTable[0] ?? 5,
    killCount: 0, damageDealt: 0,
    bossIdx: -1, bossPhase: 0, bossSpawned: false,
    waveCursor: 0, waveSpawned: new Int32Array(config.waves.length),
    flowAcc: 0, flowSeq: 0,
    characterIdx: Math.max(0, Math.min(config.characters.length - 1, init.characterIdx ?? 0)),
    lostToBoss: false,
    variant: init.variant ?? 0,
    clashThreshold: r.clashThresholdT,
    clashCrowd: -1,
    stars: 0,
    vehicleTicks: 0,
    dodgeActiveTicks: 0, dodgeCooldownTicks: 0,
    gateCursor: 0, pendingLevelUps: 0, boonWindowTicks: 0,
    rerollsLeft: 1, revivesLeft: 1, elapsedFrozenTicks: 0,
    weaponIdx: Math.max(0, Math.min(config.weapons.length - 1, config.weaponStart)), fireCooldown: 0,
    rng,
    offerKind: 0, offerOptions: new Int32Array(8), offerCount: 0,
    upgradeStacks: new Int32Array(config.upgrades.length),

    statMoveSpeed: 0, statDamage: 0, statFireRate: 0, statProjectiles: 0,
    statPierce: 0, statMaxHp: 0, statPickupRadius: 0,

    ep,
    eX: new Float64Array(ep.cap), eY: new Float64Array(ep.cap),
    eHp: new Float64Array(ep.cap), eCooldown: new Float64Array(ep.cap),
    eDefIdx: new Int32Array(ep.cap), eLane: new Int32Array(ep.cap),
    eIsBoss: new Uint8Array(ep.cap),

    pp,
    pX: new Float64Array(pp.cap), pY: new Float64Array(pp.cap),
    pVx: new Float64Array(pp.cap), pVy: new Float64Array(pp.cap),
    pLife: new Float64Array(pp.cap), pDamage: new Float64Array(pp.cap),
    pPierce: new Int32Array(pp.cap),

    qp,
    qX: new Float64Array(qp.cap), qY: new Float64Array(qp.cap),
    qVx: new Float64Array(qp.cap), qVy: new Float64Array(qp.cap),
    qLife: new Float64Array(qp.cap), qDamage: new Float64Array(qp.cap),

    kp,
    kX: new Float64Array(kp.cap), kY: new Float64Array(kp.cap),
    kKind: new Int32Array(kp.cap), kValue: new Int32Array(kp.cap),

    gp,
    gY: new Float64Array(gp.cap), gValue: new Float64Array(gp.cap),
    gHp: new Float64Array(gp.cap), gPump: new Float64Array(gp.cap),
    gGroup: new Int32Array(gp.cap), gOp: new Int32Array(gp.cap), gLane: new Int32Array(gp.cap),

    mp, mX: new Float64Array(mp.cap), mY: new Float64Array(mp.cap),
    mOp: new Int32Array(mp.cap), mValue: new Float64Array(mp.cap),
    mHp: new Float64Array(mp.cap), mHpMax: new Float64Array(mp.cap),
    mLane: new Int32Array(mp.cap), mTitan: new Int32Array(mp.cap),
    mLife: new Float64Array(mp.cap),
    monolithRaiseTick,

    lp, lX: new Float64Array(lp.cap), lY: new Float64Array(lp.cap),
    lHp: new Float64Array(lp.cap), lHpMax: new Float64Array(lp.cap),
    lCooldown: new Float64Array(lp.cap),
    lPower: new Float64Array(lp.cap), lRadius: new Float64Array(lp.cap),
    lKind: new Int32Array(lp.cap),
    // A-52 follow-up: banked shield at run start, 0 for every chapter
    // except an explicit per-chapter override (ChapterDef.startingShieldMult).
    shieldHp: r.startingShieldMult * r.squadUnitHp, shieldMax: r.startingShieldMult * r.squadUnitHp,

    sp, sX: new Float64Array(sp.cap), sY: new Float64Array(sp.cap),
    shapeScratchX: new Float64Array(sp.cap), shapeScratchY: new Float64Array(sp.cap),
    shapeScratchOldX: new Float64Array(sp.cap), shapeScratchOldY: new Float64Array(sp.cap),
    shapeScratchUsed: new Uint8Array(sp.cap),

    cellHead: new Int32Array(gridCols * gridRows).fill(-1),
    cellNext: new Int32Array(ep.cap).fill(-1),
    gridCols, gridRows, cellSize: CELL_SIZE,

    events: [],
  };
  return s;
}
